import React, { useState, useEffect } from 'react';
import api from '../services/api';

const MyRecordsPage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    course: '',
    status: '', // all, passed, failed
    sortBy: 'date', // date, score, quiz
    sortOrder: 'desc'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [statistics, setStatistics] = useState({});

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/records');
        setRecords(res.data);
        calculateStatistics(res.data);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const calculateStatistics = (recordsData) => {
    if (!recordsData.length) return;

    const totalQuizzes = new Set(recordsData.map(r => r.quizId)).size;
    const totalAttempts = recordsData.length;
    const averageScore = recordsData.reduce((sum, r) => sum + r.score, 0) / recordsData.length;
    const passedCount = recordsData.filter(r => r.passed === true).length;
    const failedCount = recordsData.filter(r => r.passed === false).length;
    const personalBests = recordsData.filter(r => r.isPersonalBest).length;

    setStatistics({
      totalQuizzes,
      totalAttempts,
      averageScore: averageScore.toFixed(1),
      passedCount,
      failedCount,
      personalBests,
      passRate: totalAttempts > 0 ? ((passedCount / totalAttempts) * 100).toFixed(1) : 0
    });
  };

  // Get unique courses for filter
  const uniqueCourses = [...new Set(records.map(r => r.quiz?.course?.title).filter(Boolean))];

  // Filter and sort records
  const filteredRecords = records
    .filter(record => {
      if (filters.course && record.quiz?.course?.title !== filters.course) return false;
      if (filters.status === 'passed' && record.passed !== true) return false;
      if (filters.status === 'failed' && record.passed !== false) return false;
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      switch (filters.sortBy) {
        case 'score':
          aVal = a.score;
          bVal = b.score;
          break;
        case 'quiz':
          aVal = a.quiz?.title || '';
          bVal = b.quiz?.title || '';
          break;
        case 'course':
          aVal = a.quiz?.course?.title || '';
          bVal = b.quiz?.course?.title || '';
          break;
        default: // date
          aVal = new Date(a.createdAt);
          bVal = new Date(b.createdAt);
      }
      
      if (filters.sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const getScoreColor = (score, passed) => {
    if (passed === true) return 'text-green-600';
    if (passed === false) return 'text-red-600';
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score, passed) => {
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    
    if (passed === true) {
      bgColor = 'bg-green-100';
      textColor = 'text-green-800';
    } else if (passed === false) {
      bgColor = 'bg-red-100';
      textColor = 'text-red-800';
    } else if (score >= 80) {
      bgColor = 'bg-green-100';
      textColor = 'text-green-800';
    } else if (score >= 60) {
      bgColor = 'bg-yellow-100';
      textColor = 'text-yellow-800';
    } else {
      bgColor = 'bg-red-100';
      textColor = 'text-red-800';
    }

    return `${bgColor} ${textColor}`;
  };

  const formatDateTime = (value) => {
    const date = new Date(value);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getRankLabel = (record) => {
    if (!record.quizRank) return null;
    if (record.quizRankTotal) return `#${record.quizRank} / ${record.quizRankTotal}`;
    return `#${record.quizRank}`;
  };

  const statTiles = [
    {
      key: 'totalQuizzes',
      label: 'Total Quizzes',
      value: statistics.totalQuizzes,
      ring: 'border-blue-100',
      bg: 'bg-blue-50',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      valueColor: 'text-blue-900',
      icon: (
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    },
    {
      key: 'averageScore',
      label: 'Average Score',
      value: `${statistics.averageScore}%`,
      ring: 'border-green-100',
      bg: 'bg-green-50',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      valueColor: 'text-green-900',
      icon: (
        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      )
    },
    {
      key: 'passRate',
      label: 'Pass Rate',
      value: `${statistics.passRate}%`,
      ring: 'border-purple-100',
      bg: 'bg-purple-50',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      valueColor: 'text-purple-900',
      icon: (
        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      key: 'personalBests',
      label: 'Personal Bests',
      value: statistics.personalBests,
      ring: 'border-orange-100',
      bg: 'bg-orange-50',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      valueColor: 'text-orange-900',
      icon: (
        <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      )
    }
  ];

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your records...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Error Loading Records</h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-indigo-50/40 p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur rounded-2xl border border-slate-200 shadow-sm px-6 py-7 text-center">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">My Quiz Results</h1>
        <p className="text-slate-600">Track your performance, review attempts, and watch your progress improve over time.</p>
        {records.length > 0 && (
          <div className="mt-4 inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-sm font-medium text-blue-700">
            {records.length} total result{records.length === 1 ? '' : 's'}
          </div>
        )}
      </div>

      {/* Statistics Cards */}
      {records.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 md:gap-3 place-items-center">
          {statTiles.map((tile) => (
            <div key={tile.key} className={`w-full max-w-[122px] md:max-w-[128px] aspect-square rounded-xl border ${tile.ring} ${tile.bg} p-2.5 md:p-3 shadow-sm flex flex-col justify-between`}>
              <div className={`w-8 h-8 rounded-lg ${tile.iconBg} flex items-center justify-center`}>
                {tile.icon}
              </div>
              <div>
                <p className="text-[10px] md:text-[11px] font-semibold text-slate-600 leading-snug">{tile.label}</p>
                <p className={`text-lg md:text-xl font-bold ${tile.valueColor} mt-0.5`}>{tile.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <button
          type="button"
          className="w-full flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left"
          onClick={() => setShowFilters((prev) => !prev)}
        >
          <span className="text-sm md:text-base font-semibold text-slate-900">Filter & Sort</span>
          <svg className={`w-5 h-5 text-slate-500 transition-transform ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showFilters && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="course-filter" className="block text-sm font-medium text-gray-700 mb-1">Course</label>
              <select
                id="course-filter"
                value={filters.course}
                onChange={(e) => setFilters(prev => ({...prev, course: e.target.value}))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Courses</option>
                {uniqueCourses.map(course => (
                  <option key={course} value={course}>{course}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                id="status-filter"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({...prev, status: e.target.value}))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Results</option>
                <option value="passed">Passed Only</option>
                <option value="failed">Failed Only</option>
              </select>
            </div>

            <div>
              <label htmlFor="sort-by-filter" className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
              <select
                id="sort-by-filter"
                value={filters.sortBy}
                onChange={(e) => setFilters(prev => ({...prev, sortBy: e.target.value}))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="date">Date</option>
                <option value="score">Score</option>
                <option value="quiz">Quiz Name</option>
                <option value="course">Course</option>
              </select>
            </div>

            <div>
              <label htmlFor="sort-order-filter" className="block text-sm font-medium text-gray-700 mb-1">Order</label>
              <select
                id="sort-order-filter"
                value={filters.sortOrder}
                onChange={(e) => setFilters(prev => ({...prev, sortOrder: e.target.value}))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Records Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 bg-slate-50/70">
          <h3 className="text-base md:text-lg font-semibold text-slate-900">Results</h3>
          <span className="text-sm text-slate-600">{filteredRecords.length} shown</span>
        </div>

        {filteredRecords.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <div className="flex flex-col items-center">
              <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-lg font-medium">No quiz records found</p>
              <p className="text-sm">Complete some quizzes to see your results here</p>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden p-4 space-y-3 bg-slate-50/30">
              {filteredRecords.map((record) => {
                const dt = formatDateTime(record.createdAt);
                return (
                  <div key={record.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{record.quiz?.title || `Quiz ${record.quizId}`}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{record.quiz?.course?.title || 'Unknown Course'}</p>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScoreBadge(record.score, record.passed)}`}>
                        {record.passed === true ? 'Passed' : record.passed === false ? 'Failed' : 'Completed'}
                      </span>
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <div className={`text-xl font-bold ${getScoreColor(record.score, record.passed)}`}>
                        {record.correct != null && record.total != null
                          ? `${record.correct}/${record.total}`
                          : record.total
                            ? `${Math.round((record.score / 100) * record.total)}/${record.total}`
                            : `${Math.round(record.score)}%`}
                      </div>
                      {record.isPersonalBest && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          Best
                        </span>
                      )}
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                      <div className="rounded-lg bg-slate-50 px-2 py-1.5">Attempts: <span className="font-medium text-slate-800">{record.userAttempts}</span></div>
                      <div className="rounded-lg bg-slate-50 px-2 py-1.5">Limit: <span className="font-medium text-slate-800">{record.quiz?.maxAttempts ? `of ${record.quiz.maxAttempts}` : 'unlimited'}</span></div>
                      {record.quizRank && (
                        <div className="rounded-lg bg-purple-50 border border-purple-100 px-2 py-1.5 col-span-2 text-purple-700">
                          Rank: <span className="font-semibold">{getRankLabel(record)}</span>
                        </div>
                      )}
                      <div className="rounded-lg bg-slate-50 px-2 py-1.5 col-span-2">{dt.date} at {dt.time}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Quiz</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Course</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Score</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Attempts</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Rank</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Achievement</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {filteredRecords.map((record) => {
                    const dt = formatDateTime(record.createdAt);
                    return (
                      <tr key={record.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-slate-900">{record.quiz?.title || `Quiz ${record.quizId}`}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-slate-600">{record.quiz?.course?.title || 'Unknown Course'}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`text-lg font-bold ${getScoreColor(record.score, record.passed)}`}>
                            {record.correct != null && record.total != null
                              ? `${record.correct}/${record.total}`
                              : record.total
                                ? `${Math.round((record.score / 100) * record.total)}/${record.total}`
                                : `${Math.round(record.score)}%`}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScoreBadge(record.score, record.passed)}`}>
                            {record.passed === true ? 'Passed' : record.passed === false ? 'Failed' : 'Completed'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-slate-900">{record.userAttempts}</div>
                          <div className="text-xs text-slate-500">
                            {record.quiz?.maxAttempts ? `of ${record.quiz.maxAttempts}` : 'unlimited'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {record.quizRank ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                              {getRankLabel(record)}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-slate-900">{dt.date}</div>
                          <div className="text-xs text-slate-500">{dt.time}</div>
                        </td>
                        <td className="px-6 py-4">
                          {record.isPersonalBest && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              Best Score
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Results Summary */}
      {filteredRecords.length > 0 && (
        <div className="text-center text-sm text-slate-500">
          Showing {filteredRecords.length} of {records.length} records
        </div>
      )}
      </div>
    </div>
  );
};

export default MyRecordsPage;
